import { createGlobalStyle } from 'styled-components';
import { theme } from './tokens';

/**
 * Flattens the theme object into CSS custom properties declared on :root so
 * plain `.css` files (BattleLayout.css, etc.) can read them without importing
 * the JS theme.
 *
 * IMPORTANT for Phase 1: this file is purely additive. It declares variables
 * and mounts the grain overlay element with `opacity: 0` by default. No
 * existing visible styling changes until Phase 2+ components start consuming
 * `var(--color-red-600)` etc. and setting `--grain-opacity` on `body` or a
 * route container.
 */

const flattenPalette = () => {
    const lines = [];
    Object.entries(theme.color).forEach(([family, shades]) => {
        if (family === 'semantic') {
            Object.entries(shades).forEach(([k, v]) => {
                lines.push(`--color-${k}: ${v};`);
            });
            return;
        }
        Object.entries(shades).forEach(([step, hex]) => {
            lines.push(`--color-${family}-${step}: ${hex};`);
        });
    });
    return lines.join('\n  ');
};

const flattenFonts = () => {
    const f = theme.font;
    const lines = [
        `--font-display: ${f.family.display};`,
        `--font-heading: ${f.family.heading};`,
        `--font-body: ${f.family.body};`,
        `--font-mono: ${f.family.mono};`,
        `--font-tamil: ${f.family.tamil};`,
    ];
    Object.entries(f.size).forEach(([k, v]) => {
        lines.push(`--size-${k}: ${v};`);
    });
    Object.entries(f.weight).forEach(([k, v]) => {
        lines.push(`--weight-${k}: ${v};`);
    });
    Object.entries(f.tracking).forEach(([k, v]) => {
        lines.push(`--tracking-${k}: ${v};`);
    });
    return lines.join('\n  ');
};

const flattenRadii = () =>
    Object.entries(theme.radius)
        .map(([k, v]) => `--radius-${k}: ${v};`)
        .join('\n  ');

const flattenShadows = () =>
    Object.entries(theme.shadow)
        .map(([k, v]) => `--shadow-${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v};`)
        .join('\n  ');

const flattenSpacing = () =>
    Object.entries(theme.spacing)
        .map(([k, v]) => `--space-${k}: ${v};`)
        .join('\n  ');

const flattenMotion = () => {
    const m = theme.motion;
    const lines = [
        `--ease-out: ${m.ease.out};`,
        `--ease-in-out: ${m.ease.inOut};`,
        `--ease-bounce: ${m.ease.bounce};`,
    ];
    Object.entries(m.duration).forEach(([k, v]) => {
        lines.push(`--dur-${k}: ${v}ms;`);
    });
    return lines.join('\n  ');
};

const flattenClip = () =>
    Object.entries(theme.clip)
        .map(([k, v]) => `--clip-${k}: ${v};`)
        .join('\n  ');

export const GlobalStyle = createGlobalStyle`
  :root {
    ${flattenPalette()}

    ${flattenFonts()}

    ${flattenRadii()}

    ${flattenShadows()}

    ${flattenSpacing()}

    ${flattenMotion()}

    ${flattenClip()}

    --grain-url: url('${theme.grain.grainUrl}');
    --halftone-url: url('${theme.grain.halftoneUrl}');

    /* Set by a route-level container (e.g. homepage = 0.18, arena = 0.12).
       Default 0 so Phase 1 is a no-op visually. */
    --grain-opacity: 0;
    --halftone-opacity: 0;
  }

  /* Grain overlay - a single shared texture applied over the entire viewport.
     Phase 1 ships it invisible (opacity 0). Phases 2+ will set the
     --grain-opacity variable on body or a route container to reveal it. */
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    background-image: var(--grain-url);
    background-repeat: repeat;
    opacity: var(--grain-opacity);
    mix-blend-mode: overlay;
    z-index: 9999;
    will-change: opacity;
    transition: opacity var(--dur-slow) var(--ease-in-out);
  }

  /* Halftone overlay - only revealed on the homepage, behind the wordmark.
     Applied via a scoped container setting --halftone-opacity. */
  body::after {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    background-image: var(--halftone-url);
    background-repeat: repeat;
    opacity: var(--halftone-opacity);
    mix-blend-mode: multiply;
    z-index: 9998;
    will-change: opacity;
    transition: opacity var(--dur-slow) var(--ease-in-out);
  }

  /* Respect reduced motion - kill the two ambient overlays' transitions. */
  @media (prefers-reduced-motion: reduce) {
    body::before,
    body::after {
      transition: none;
    }
  }
`;

export default GlobalStyle;
